import { Input, Button, Select, DatePicker, InputNumber, Tooltip, ConfigProvider } from 'antd';
import { useEffect, useState, useRef, useMemo } from "react";
import type { InputRef, RefSelectProps } from 'antd';
import { PlusOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import { IOption } from "../../selects/select";
import GridWrapper from "../../controls/GridWrapper";
import { CalendarIcon } from '../../../libs/corporate/icons/outlined/action/MainCollection';
import ruRU from "antd/lib/locale/ru_RU"
import moment from "moment";
import { DateTime } from "luxon";
import { } from "../../../Services/Extensions/DateTime";
import styles from '../styles/SearchModalWindowAntd.module.scss'
import { defaultColumn, defaultOperator } from "../../../@types/enumsGlobal";
import BaseIconButton from '../../buttons/iconButtons/BaseIconButton';
import { MultiplyIcon } from '../../../libs/corporate/icons/outlined/suggested/SymbolCollection';
import { useAppContext } from '../../../system/providers/appContextProvider';
import { ContractorDataProvider } from '../../../Services/DataProviders/ContractorDataProvider';
import { IContractorDTO } from '../../../libs/coreapi-dto/dirs/contractor';
import { StoreDataProvider } from '../../../Services/DataProviders/StoreDataProvider';
import { applyConversion } from '../../../system/functions/conversion';

type PropertyType = 'string' | 'number' | 'boolean' | 'date' | 'datetime' | 'decimal' | "enum";

export interface ISearchColumn {
    propertyName: string;
    propertyType: PropertyType;
    displayName: string;
    hiddenFromUI?: boolean,
    conversion?: ColumnType
}

interface ISearchModalWindowAntdProps {
    columns: ISearchColumn[];
    defaultFilters?: IGridColumnFilter[];
    search?: (columnFilters: IGridColumnFilter[]) => void;
    cancel?: () => void;
    open?: boolean;
    onCancel?: () => void;
}

const SELECTABLE_FIELDS = {
    documentState: {
        options: [
            { value: 'draft', label: 'Черновик' },
            { value: 'proc', label: 'Отработан' },
            { value: 'secured', label: 'Обеспечен' },
            { value: 'del', label: 'Удален' }
        ] as const,
        placeholder: 'Выберите статус' as const
    },
    disposalStatus: {
        options: [
            { value: 'allDisposed', label: 'Выбыт' },
            { value: 'partialDisposed', label: 'Выбыт частично' },
            { value: 'noneDisposed', label: 'Не выбыт' },
            { value: 'unprocDisposal', label: 'Не отработан'},
            { value: 'inProgressDisposal', label: 'Идёт отправка' }
        ] as const,
        placeholder: 'Выберите статус' as const
    }
} as const;

const DEFAULT_PROPERTY_NAME = ['mnemocode', 'name']

const SearchModalWindowAntd: React.FC<ISearchModalWindowAntdProps> = ({
    open,
    onCancel,
    ...props
}) => {
    const appContext = useAppContext()
    const contractorDP = new ContractorDataProvider(appContext.coreApiService)
    const storeDataProvider = new StoreDataProvider(appContext.coreApiService)
    
    const hiddenColumns = new Set([
        "idContractorGlobal",
        "idStoreGlobal",
        "idSupplierGlobal",
        "isAcceptRequestForMovement",
        "numerator",
        "denominator",
        "mnn"
    ]);

    const columnsWithExtra = useMemo(() => {
        const extraColumns: ISearchColumn[] = [
            {
                propertyName: "idContractorGlobal",
                propertyType: "string",
                displayName: "Аптека",
            },
            {
                propertyName: "idSupplierGlobal",
                propertyType: "string",
                displayName: "Поставщик",
            },            
            {
                propertyName: "isAcceptRequestForMovement",
                propertyType: "boolean",
                displayName: "Разрешено для перемещения",
            },            
            {
                propertyName: "mnn",
                propertyType: "boolean",
                displayName: "МНН",
            },            
            {
                propertyName: "numerator",
                propertyType: "number",
                displayName: "Нумератор",
                hiddenFromUI: true, // скрываем из UI
            },            
            {
                propertyName: "denominator",
                propertyType: "number",
                displayName: "Деноминатор",
                hiddenFromUI: true, // скрываем из UI
            },
        ];
    
        const existingPropertyNames = props.columns.map(col => col.propertyName);
    
        const missingColumns = extraColumns.filter(col => !existingPropertyNames.includes(col.propertyName));
        
        return [...props.columns, ...missingColumns];
    }, [props.columns]);

    
    
    const isProtectedDateFilter = (filter: IGridColumnFilter) => {
        const isDateField = columnsWithExtra?.some(c =>
            c.propertyName === filter.name &&
            (c.propertyType === "date" || c.propertyType === "datetime")
        );
        const isProtectedOperator = ["MoreOrEq", "LessOrEq", "Less"].includes(filter.operator);
        return isDateField && isProtectedOperator;
    };
    
    const getDefaultColumn = (propertyNames: string[]) => {
        const lowerCasePropertyNames = propertyNames.map(name => name.toLowerCase());
        
        let foundColumn = columnsWithExtra.find(c => 
            lowerCasePropertyNames.includes(c.propertyName.toLowerCase())
        );
        
        return foundColumn || columnsWithExtra[0];
    };

    const [columnFilters, setColumnFilters] = useState<IGridColumnFilter[]>(() => {
        const initialFilters = props.defaultFilters ?? [];
        return initialFilters.map(filter => ({
            ...filter,
            protected: isProtectedDateFilter(filter)
        }));
    });
    const defaultBooleanOption = { displayName: "Выберите", value: "", hidden: true } as IOption;
    const columnOptions: IOption[] = columnsWithExtra
    .filter(col => !hiddenColumns.has(col.propertyName))
    .map(col => ({
        ...col,
        value: col.propertyName,
    }));
    const [column, setColumn] = useState<ISearchColumn>(getDefaultColumn(DEFAULT_PROPERTY_NAME));
    const [operatorOptions, setOperatorOptions] = useState<IOption[]>([] as IOption[]);
    const [operator, setOperator] = useState<OperatorType>();
    const [query, setQuery] = useState<any>(undefined);
    const [selectedDate, setSelectedDate] = useState<DateTime | null>(null);

    const inputNumRef = useRef<InputRef>(null);
    const inputRef = useRef<InputRef>(null);    
    const selectRef = useRef<RefSelectProps>(null);
    const pickerRef = useRef<any>(null);

    const isFilterConditionDuplicate = (newFilter: Omit<IGridColumnFilter, 'value'>) => {
        return columnFilters.some(existingFilter => 
            existingFilter.name === newFilter.name && 
            existingFilter.operator === newFilter.operator
        );
    };
    const [contractorsMap, setContractorsMap] = useState<Record<string, string>>({});
    const [storesMap, setStoresMap] = useState<Record<string, string>>({});
    
    

    useEffect(() => {
        const loadContractorNames = () => {
            const contractorFilters = columnFilters.filter(f => f.name === "idContractorGlobal");
    
            contractorFilters.forEach(filter => {
                const id = filter.value;
                
                if (contractorsMap[id]) return;    
               
                contractorDP.getById(id, (entity) => {
                    setContractorsMap(prev => ({
                        ...prev,
                        [id]: entity.name
                    }));
                });
            });
        };    
        loadContractorNames();
    }, [columnFilters, contractorsMap]);

    useEffect(() => {
        const loadStoreNames = () => {
            const storeFilters = columnFilters.filter(f => f.name === "idStoreGlobal");
    
            storeFilters.forEach(filter => {
                const id = filter.value;
                
                if (storesMap[id]) return;    
                
                storeDataProvider.getById(id, (entity) => {                    
                    setStoresMap(prev => ({
                        ...prev,
                        [id]: entity.name
                    }));
                });
            });
        };    
        loadStoreNames();
    }, [columnFilters, storesMap]);

    useEffect(() => {
        if (open || column.displayName) {
            const timer = setTimeout(() => {
                inputRef.current?.focus();
                inputNumRef.current?.focus();
                selectRef.current?.focus();
                pickerRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer)
        }
    }, [open, column.displayName])

    useEffect(() => {
        const allOperators = [
            { displayName: "Содержит", value: "Like", propertyTypes: ["string"] },
            { displayName: "Равно", value: "Eq", propertyTypes: ["string", "number", "boolean", "date", "datetime"] },
            { displayName: "Не равно", value: "NotEq", propertyTypes: ["string", "number", "boolean", "date", "datetime"] },
            { displayName: "Больше", value: "More", propertyTypes: ["number", "date", "datetime"] },
            { displayName: "Больше либо равно", value: "MoreOrEq", propertyTypes: ["number", "date", "datetime"] },
            { displayName: "Меньше", value: "Less", propertyTypes: ["number", "date", "datetime"] },
            { displayName: "Меньше либо равно", value: "LessOrEq", propertyTypes: ["number", "date", "datetime"] },
        ];
        const operatorList: IOption[] = allOperators.filter((item) => item.propertyTypes.indexOf(column.propertyType) >= 0);
        setOperatorOptions([...operatorList]);
        setQuery("");
    }, [column]);

    useEffect(() => {
        if (operatorOptions?.length > 0) setOperator(operatorOptions[0].value);
    }, [operatorOptions]);

    const booleanTranslations = {
        "true": "Да",
        "false": "Нет",
    };   
   
    const formatFilterValue = (value: string, propertyType: string, propertyName?: string, operator?: string) => {

        const cleanValue = operator === "Like"
            ? value.replace(/^%|%$/g, '')
            : value;
        if (propertyName === "idContractorGlobal") {
            return contractorsMap[value] || value;
        }
        if (propertyName === "idStoreGlobal") {
            return storesMap[value] || value;
        }

        if (propertyName && propertyName in SELECTABLE_FIELDS) {
            const option = SELECTABLE_FIELDS[propertyName].options.find(
                opt => opt.value === cleanValue
            );
            return option?.label || cleanValue;
        }

        if ((propertyType === "date" || propertyType === "datetime") && cleanValue) {
            const dateFormats = ["yyyyMMddHHmmss", "yyyyMMdd"];
            const dateValue = dateFormats
                .map(format => DateTime.fromFormat(cleanValue, format))
                .find(date => date.isValid);

            return dateValue?.toFormat("dd.MM.yyyy") ?? cleanValue;
        }

        if (propertyType === "string") {
            return cleanValue;
        }

        return booleanTranslations[value] || value;
    };

    const handleClear = () => {
        const preservedFilters = columnFilters.filter(isProtectedDateFilter);

        setColumnFilters(preservedFilters);        

        setQuery(undefined);
        setSelectedDate(null);

        setColumn(columnsWithExtra[0]);
        setOperator(operatorOptions[0]?.value);
    };

    const handleDateChange = (date: moment.Moment | null) => {
        const luxonDate = date ? DateTime.fromJSDate(date.toDate()) : null;
        setSelectedDate(luxonDate);
        setQuery(luxonDate);
    };

    const text = <span>Очистить</span>

    const ClearButton = () => {
        return (
            <Tooltip
                placement="bottom"
                title={text}
                overlayStyle={{
                    maxWidth: "200px",
                    padding: "5px 8px",                   
                }}
                overlayInnerStyle={{
                    color: "#fff",
                    fontSize: "13px",
                    fontWeight: "400",
                    borderRadius: "4px",
                    textAlign: "center",
                    padding: "3px 8px",
                    borderStyle: "solid",
                    borderWidth: "1px",
                    borderColor: "#343f10",
                }}
            >
                <div className={styles.clearButton}>
                    <BaseIconButton tabIndex={-1} sizeVariant={"mini"}>
                        <MultiplyIcon />
                    </BaseIconButton>
                </div>
            </Tooltip>
        )
    }

    const renderFieldSelect = () => (
        <div className={styles.selectContainer}>
            <Select
                getPopupContainer={() => document.body}
                popupClassName="myCustomDropdown"
                className={styles.selectField}
                labelInValue
                placeholder="Поле"
                value={column.propertyName ? { value: column.propertyName, label: column.displayName } : undefined}
                onChange={(value: { value: string; label: string }) => {
                    const selectedColumn = columnsWithExtra.find(c => c.propertyName === value.value);
                    if (selectedColumn) {
                        setColumn(selectedColumn);
                        if (selectedColumn.propertyType === "boolean") setQuery(defaultBooleanOption);
                    }
                }}
                options={columnOptions.map(option => ({
                    value: option.value,
                    label: option.displayName,
                    disabled: option.hidden
                }))}
            />
        </div>
    );

    const renderOperatorSelect = () => (
        <div className={styles.selectContainer}>
            <Select
                getPopupContainer={() => document.body}                
                className={styles.selectField}
                placeholder="Оператор"
                value={operator}
                onChange={(value: OperatorType) => setOperator(value)}
                options={operatorOptions.map(option => ({
                    value: option.value,
                    label: option.displayName,
                    disabled: option.hidden
                }))}
            />
        </div>

    );

    const renderBooleanSelect = () => (
        <div className={styles.selectContainer}>
            <Select
                getPopupContainer={() => document.body}
                ref={selectRef}
                autoFocus
                className={styles.selectField}
                value={query}
                onChange={value => setQuery(value === "" ? undefined : value)}
                options={[
                    defaultBooleanOption,
                    { value: true, label: "Да" },
                    { value: false, label: "Нет" }
                ]}
            />
        </div>
    );

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            if (
                !isFilterConditionDuplicate({
                    name: column.propertyName,
                    operator: operator as OperatorType,
                    invisible: false,
                }) &&
                ((column.propertyType === "date" || column.propertyType === "datetime") 
                    ? selectedDate 
                    : query !== undefined)
            ) {
                let filterValue = query;
                if (column.propertyType === "number" && typeof filterValue === 'string') {
                    filterValue = filterValue.replace(',', '.');
                }
                if ((column.propertyType === "date" || column.propertyType === "datetime") && selectedDate) {
                    filterValue = selectedDate.toFormat("yyyyMMdd");
                }

                const newFilters = [
                    ...columnFilters,
                    {
                        name: column.propertyName,
                        value: operator === "Like" ? `%${filterValue}%` : filterValue,
                        operator: operator as OperatorType,
                        invisible: false,
                    },
                ];

                setColumnFilters(newFilters);
                setQuery(undefined);
                setSelectedDate(null);

                props.search?.(newFilters);
                onCancel?.();
            }
        }
    };

    const handleKeyDownForNum = (
        e: React.KeyboardEvent<HTMLInputElement>,
        value: string,
        setQuery: (val: string) => void
    ) => {
        const input = e.currentTarget;
        const cursorPos = input.selectionStart ?? 0;
        const dotIndex = value.indexOf(',');
        const decimalPart = value.split(',')[1] ?? '';    
        const isEditingDecimal = dotIndex !== -1 && cursorPos > dotIndex;
        const isAllowedKey = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Enter', 'Tab'].includes(e.key);    
        if (e.key === '.' || e.key === ',') {
            e.preventDefault();        
            if (value.includes(',')) {
                return;
            }
            const left = value.slice(0, cursorPos);
            const right = value.slice(cursorPos);
            setQuery(left + ',' + right);
            return;
        }
        if (e.key === ',' && value.includes(',')) {
            e.preventDefault();
            return;
        }    
        if (
            isEditingDecimal &&
            decimalPart.length >= 2 &&
            !isAllowedKey
        ) {
            e.preventDefault();
        }    
        const isValidChar = /^[\d,-]$/.test(e.key);
        if (!isAllowedKey && !isValidChar) {
            e.preventDefault();
        }    
        if (e.key === '-' && cursorPos !== 0) {
            e.preventDefault();
        }
    };

    return (
        <div className={`${styles.modalWindow} ${open ? styles.visible : ''}`}>
            <div className={styles.headerWrapper}>
                <div className={styles.closeButtonRow}>
                    <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={onCancel}
                        className={styles.closeButton}
                    />
                </div>
                <GridWrapper cols={4} className={styles.gridWrapper}>
                    <div className={styles.selectContainer}>
                        <div className={styles.selectLabel}>Поле</div>
                        {renderFieldSelect()}
                    </div>
                    <div className={styles.selectContainer}>
                        <div className={styles.selectLabel}>Оператор</div>
                        {renderOperatorSelect()}
                    </div>
                    {column.propertyType === "string" && SELECTABLE_FIELDS[column.propertyName as keyof typeof SELECTABLE_FIELDS] ? (
                        <div className={styles.selectContainer}>
                            <Select
                                getPopupContainer={() => document.body}
                                ref={selectRef}
                                className={styles.selectField}
                                value={query}
                                placeholder={SELECTABLE_FIELDS[column.propertyName as keyof typeof SELECTABLE_FIELDS].placeholder}
                                onChange={(value) => setQuery(value)}
                                options={SELECTABLE_FIELDS[column.propertyName]?.options ?? []}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    ) : column.propertyType === "string" ? (
                        <Input
                            ref={inputRef}
                            className={styles.customInput}
                            value={query}
                            placeholder="Искать..."
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                    ) : null}

                    {column.propertyType === "number" && (
                        <Input
                            ref={inputNumRef}
                            value={query}
                            className={styles.customInput}
                            placeholder="Искать..."
                            onChange={(e) => {
                                let stringValue = e.target.value;
                                stringValue = stringValue.replace(/\./g, ',');
                                const parts = stringValue.split(',');
                                if (parts.length > 2) {
                                    stringValue = parts[0] + ',' + parts[1];
                                }
                                if (parts[1]?.length > 2) {
                                    stringValue = parts[0] + ',' + parts[1].slice(0, 2);
                                }
                                if (stringValue.includes('-') && !stringValue.startsWith('-')) {
                                    stringValue = '-' + stringValue.replace(/-/g, '');
                                }
                                setQuery(stringValue);
                            }}
                            onKeyDown={(e) => {
                                handleKeyDownForNum(e, query ?? '', setQuery);
                                handleKeyDown(e);
                            }}
                        />
                    )}

                    {column.propertyType === "boolean" && (
                        <div className={styles.selectContainer}>
                            <div className={styles.selectLabel}>Искать</div>
                            {renderBooleanSelect()}
                        </div>
                    )}

                    {(column.propertyType === "date" || column.propertyType === "datetime") && (
                        <ConfigProvider locale={ruRU}>
                            <DatePicker
                                getPopupContainer={() => document.body}
                                ref={pickerRef}
                                className={`${styles.customDatePicker} ${styles.customPadding}`}
                                value={selectedDate ? moment(selectedDate.toJSDate()) : null}
                                format={'DD.MM.YYYY'}
                                suffixIcon={<CalendarIcon />}
                                clearIcon={<ClearButton />}
                                onChange={handleDateChange}
                                onBlur={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    if (value.length >= 6) {
                                        const day = value.slice(0, 2);
                                        const month = value.slice(2, 4);
                                        const year = value.slice(4, 8); // Обрезаем до 4 цифр
                                        const formatted = `${day}.${month}.${year}`;
                                        e.target.value = formatted;
                                        const date = moment(formatted, 'DD.MM.YYYY');
                                        setSelectedDate(date.isValid() ? DateTime.fromJSDate(date.toDate()) : null);
                                    }
                                }}
                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                    const input = e.target as HTMLInputElement;
                                    const rawValue = input.value;
                                    const digitsOnly = rawValue.replace(/\D/g, '');                            
                                    
                                    if (digitsOnly.length >= 8 && /\d/.test(e.key) && !['Backspace', 'Delete'].includes(e.key)) {
                                        e.preventDefault();
                                        return;
                                    }                            
                                    
                                    if (/\D/.test(e.key) && !['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter', '.'].includes(e.key)) {
                                        e.preventDefault();
                                        return;
                                    }                            
                                
                                    if (e.key !== 'Backspace') {                                   
                                        if (/^\d{2}$/.test(rawValue)) {
                                            input.value = rawValue + '.';
                                        } 
                                        else if (/^\d{2}\.\d{2}$/.test(rawValue)) {
                                            input.value = rawValue + '.';
                                        }
                                    }
                                
                                    handleKeyDown(e);
                                }}
                            />
                        </ConfigProvider>
                    )}

                    <Tooltip
                        overlayInnerStyle={{
                            borderRadius: "4px"
                        }}
                        title={isFilterConditionDuplicate({
                            name: column.propertyName,
                            operator: operator as OperatorType,
                            invisible: false,
                        }) ? "Такое условие фильтрации уже добавлено" : ""}                   
                    >
                        <Button
                            className={styles.addFilterButton}
                            style={{ minWidth: '36px', width: '36px', height: '36px', borderRadius: '4px' }}
                            icon={<PlusOutlined />}
                            disabled={
                                isFilterConditionDuplicate({
                                    name: column.propertyName,
                                    operator: operator as OperatorType,
                                    invisible: false,
                                }) ||
                                ((column.propertyType === "date" || column.propertyType === "datetime") 
                                    ? !selectedDate
                                    : !query && query !== false)
                            }
                            onClick={() => {
                                let filterValue = query;
                                if ((column.propertyType === "date" || column.propertyType === "datetime") && selectedDate) {
                                    filterValue = selectedDate.toFormat("yyyyMMdd");
                                }

                                if (column.propertyType === "number" && typeof filterValue === 'string') {
                                    filterValue = filterValue.replace(',', '.');
                                }
                                
                                filterValue = applyConversion(column, filterValue);
                                setColumnFilters([
                                    ...columnFilters,
                                    {
                                        name: column.propertyName,
                                        value: operator === "Like" ? `%${filterValue}%` : filterValue,
                                        operator: operator as OperatorType,
                                        invisible: false,
                                    },
                                ]);
                                setQuery(undefined);
                                setSelectedDate(null);
                            }}
                        />
                    </Tooltip>
                </GridWrapper>
            </div>
            <div className={styles.filtersListWrapper}>
                {columnFilters
                    .filter(item => {
                        const column = columnsWithExtra?.find(col => col.propertyName === item.name);
                        return !column?.hiddenFromUI;
                    }) // скрываем технические фильтры из списка
                    .map((item, index) => {
                        const isProtected = isProtectedDateFilter(item);
                        const columnForFilter = columnsWithExtra?.find(el => el.propertyName === item.name);
                        const propertyType = columnForFilter?.propertyType || "";
                        return (
                            <div
                                className={`${styles.wrapperFilter} ${isProtected ? styles.protectedFilter : ''}`}
                                key={index}
                            >
                                <GridWrapper cols={4} className={styles.gridWrapper}>
                                    <div>{columnsWithExtra?.find(el => el.propertyName === item.name)?.displayName ?? defaultColumn[item.name]}</div>
                                    <div>{defaultOperator[item.operator]}</div>
                                    <div>{formatFilterValue(item.value, propertyType, item.name, item.operator)}</div>
                                    <Tooltip title={isProtected ? "Этот фильтр нельзя удалить" : ""}>
                                        <Button
                                            className={styles.deleteFilterButton}
                                            style={{ minWidth: '36px', width: '36px', height: '36px', borderRadius: '4px' }}
                                            icon={<DeleteOutlined />}
                                            danger
                                            disabled={isProtectedDateFilter(item)}
                                            onClick={() => {
                                                if (!isProtectedDateFilter(item)) {
                                                    setColumnFilters(columnFilters.filter((_, i) => i !== index));
                                                }
                                            }}
                                        />
                                    </Tooltip>
                                </GridWrapper>
                            </div>
                        );
                    })}

                <div className={styles.buttonRow}>
                    <Button
                        className={styles.clearFiltersButton}
                        danger
                        onClick={handleClear}
                        disabled={columnFilters.length === 0 && !query && !selectedDate}
                    >
                        Очистить
                    </Button>
                    <Button
                        className={styles.applyFiltersButton}
                        type="primary"
                        onClick={() => {
                            const isDate = column.propertyType === "date" || column.propertyType === "datetime";
                            const isDuplicate = isFilterConditionDuplicate({
                                name: column.propertyName,
                                operator: operator as OperatorType,
                                invisible: false,
                            });
                            const hasValue = isDate ? !!selectedDate : (!!query || query === false);
                            const isAddButtonEnabled = !isDuplicate && hasValue;

                            let filtersToApply = columnFilters;
                            if (isAddButtonEnabled) {
                                let filterValue = query;
                                if (isDate && selectedDate) {
                                    filterValue = selectedDate.toFormat("yyyyMMdd");
                                }
                                if (column.propertyType === "number" && typeof filterValue === 'string') {
                                    filterValue = filterValue.replace(',', '.');
                                }
                                filterValue = applyConversion(column, filterValue);
                                filtersToApply = [
                                    ...columnFilters,
                                    {
                                        name: column.propertyName,
                                        value: operator === "Like" ? `%${filterValue}%` : filterValue,
                                        operator: operator as OperatorType,
                                        invisible: false,
                                    },
                                ];
                                setColumnFilters(filtersToApply);
                            }

                            props.search?.(filtersToApply);
                            onCancel?.();
                            setQuery(undefined);
                            setSelectedDate(null);
                        }}
                    >
                        Применить
                    </Button>
                </div>
            </div>
        </div>

    );
};
export default SearchModalWindowAntd
